import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/client/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/client/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/client/components/ui/popover';
import { cn } from '@/client/lib/utils';

type Props = {
  value: string;
  onChange: (value: string) => void;
  categories: string[];
  placeholder?: string;
};

export function CategoryCombobox({
  value,
  onChange,
  categories,
  placeholder = 'Select or type category…',
}: Props) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const filtered = categories.filter((c) => c.toLowerCase().includes(inputValue.toLowerCase()));

  function handleSelect(name: string) {
    onChange(name);
    setOpen(false);
    setInputValue('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (
      e.key === 'Enter' &&
      inputValue &&
      !filtered.some((c) => c.toLowerCase() === inputValue.toLowerCase())
    ) {
      handleSelect(inputValue);
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setInputValue('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        className="p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type new…"
            value={inputValue}
            onValueChange={setInputValue}
            onKeyDown={handleKeyDown}
          />
          <CommandList>
            <CommandEmpty>Press Enter to add &ldquo;{inputValue}&rdquo;</CommandEmpty>
            <CommandGroup>
              {filtered.map((cat) => (
                <CommandItem key={cat} value={cat} onSelect={() => handleSelect(cat)}>
                  <Check
                    className={cn('mr-2 h-4 w-4', value === cat ? 'opacity-100' : 'opacity-0')}
                  />
                  {cat}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
